import { CartLine, CartProduct } from "../types/cart";
import SaleOrderServices from "../services/SalesOrderService";
import { calculateCartTotals, effectiveCap } from "./cartPersistence";

export type CheckoutGateResult =
  | { ok: true; items: CartLine[] }
  | {
      ok: false;
      reason:
        | "invalid_price"
        | "empty"
        | "validate_failed"
        | "empty_after_update"
        | "changed";
      removed: number;
      adjusted: number;
      priceChanged: number;
    };

function asProductRecord(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object") return {};
  const rec = data as Record<string, unknown>;
  if (rec.data && typeof rec.data === "object") {
    return rec.data as Record<string, unknown>;
  }
  return rec;
}

export function productFromApi(
  data: unknown,
  fallback: CartProduct
): CartProduct {
  const rec = asProductRecord(data);
  const price = Number(rec.price ?? rec.Price ?? fallback.price);
  let stock = Number(rec.stock ?? rec.Stock ?? fallback.stock);
  const maxQty = Number(
    rec.maxQtyPerCustomer ?? rec.MaxQtyPerCustomer ?? fallback.maxQtyPerCustomer
  );
  if (!Number.isFinite(stock)) stock = fallback.stock;
  // GET /products/{id} returns the RAW warehouse stock. The product list (products/searchproducts)
  // already applied Item Control — "allowed − consumed" for quantity rules — and that value is what
  // `fallback.stock` carries. Taking the raw number used to wipe the cap on every Add and at
  // checkout, so customers could order any quantity. The refresh may only tighten the limit.
  const listedStock = Number(fallback.stock);
  if (Number.isFinite(listedStock) && listedStock >= 0) {
    stock = Math.min(stock, listedStock);
  }
  return {
    ...fallback,
    id: String(rec.id ?? rec.Id ?? fallback.id),
    name: String(rec.name ?? rec.Name ?? rec.title ?? fallback.name),
    title: String(rec.title ?? rec.Title ?? rec.name ?? fallback.title),
    code: String(rec.code ?? rec.Code ?? fallback.code),
    stock,
    batch: rec.batch ? String(rec.batch) : fallback.batch,
    expiryDate: rec.expiryDate ? String(rec.expiryDate) : fallback.expiryDate,
    price: Number.isFinite(price) ? price : fallback.price,
    category: String(rec.category ?? rec.Category ?? fallback.category),
    maxQtyPerCustomer:
      Number.isFinite(maxQty) && maxQty > 0 ? maxQty : fallback.maxQtyPerCustomer,
    description: rec.description
      ? String(rec.description)
      : fallback.description,
  };
}

export async function refreshProductForAdd(
  product: CartProduct
): Promise<CartProduct> {
  try {
    const res = await SaleOrderServices.getProductById(product.id);
    return productFromApi(res.data, product);
  } catch {
    return product;
  }
}

export async function runCheckoutGate(
  items: CartLine[],
  apply: (next: CartLine[]) => void
): Promise<CheckoutGateResult> {
  if (items.some((line) => Number(line.product.price) <= 0)) {
    return { ok: false, reason: "invalid_price", removed: 0, adjusted: 0, priceChanged: 0 };
  }
  if (items.length === 0) {
    return { ok: false, reason: "empty", removed: 0, adjusted: 0, priceChanged: 0 };
  }

  const uniqueIds = Array.from(new Set(items.map((line) => line.product.id)));
  let fetched: Record<string, CartProduct> = {};
  try {
    const results = await Promise.all(
      uniqueIds.map(async (id) => {
        const fallback = items.find((l) => l.product.id === id)!.product;
        const res = await SaleOrderServices.getProductById(id);
        return [id, productFromApi(res.data, fallback)] as const;
      })
    );
    fetched = Object.fromEntries(results);
  } catch {
    return {
      ok: false,
      reason: "validate_failed",
      removed: 0,
      adjusted: 0,
      priceChanged: 0,
    };
  }

  let removed = 0;
  let adjusted = 0;
  let priceChanged = 0;
  const next: CartLine[] = [];

  for (const line of items) {
    const fresh = fetched[line.product.id] ?? line.product;
    if (Number(fresh.price) <= 0) {
      removed += 1;
      continue;
    }
    const cap = effectiveCap(fresh);
    if (cap < 1) {
      removed += 1;
      continue;
    }
    const qty = Math.min(line.quantity, cap);
    if (qty !== line.quantity) adjusted += 1;
    if (Number(fresh.price) !== Number(line.product.price)) priceChanged += 1;
    next.push({ product: fresh, quantity: qty });
  }

  apply(calculateCartTotals(next).items);

  if (next.length === 0) {
    return { ok: false, reason: "empty_after_update", removed, adjusted, priceChanged };
  }
  if (removed || adjusted || priceChanged) {
    return { ok: false, reason: "changed", removed, adjusted, priceChanged };
  }
  return { ok: true, items: next };
}
